import React from 'react';
import { InkindPageAPIModel } from '@gik/core/models/gik/InkindPage';
import { Button, FontIcon, Tooltip, UI } from '@gik/ui';
import bemBlock from '@gik/core/utils/bemBlock';
import UserPageCard from '@gik/user-profile/components/UserPages/UserPageCard';
import UserPagesListSkeleton from './UserPagesListSkeleton';
import { useUserStore } from '@gik/core/store/UserStore';
import withSuspense from '@gik/core/components/suspended/withSuspense';
import { useUserPages } from '@gik/api/inkinds/userPages';
import { removeOrUnfollowPage } from '@gik/api/users/user';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import { optimisticUpdate } from '@gik/core/api';

export interface IUserPagesListProps {
  items?: InkindPageAPIModel[];
  onChange?(userPages: InkindPageAPIModel[]): void;
}

const blockName = 'user-pages-list';

function UserPagesList({ items, onChange }: IUserPagesListProps) {
  const bem = bemBlock(blockName);

  const userStore = useUserStore();
  const { data: userPages, error: userPagesError, mutate, revalidate } = useUserPages(userStore.id, {
    initialData: items,
  });

  React.useEffect(() => {
    if (onChange) {
      onChange(userPages);
    }
  }, [userPages]);

  const pages = userPages;

  const isEmpty = pages?.length === 0;

  if (!userPages) {
    return <UserPagesListSkeleton />;
  }

  async function handleRemove(inkindPage: InkindPageAPIModel) {
    const canEdit = canUserEditPage(inkindPage, userStore.id);

    const response = await UI.confirm(
      canEdit
        ? 'Are you sure you want to permanently deactivate this page? The page will no longer be available to any visitors.'
        : 'Are you sure you want to stop following this page?'
    );

    // stop if the user cancelled the confirmation dialog
    if (!response) {
      return;
    }

    const newData = userPages.filter(page => page.id !== inkindPage.id);
    optimisticUpdate(newData, mutate, () => removeOrUnfollowPage(userStore.id, inkindPage.routeId));
  }

  const isLoading = !userPages;
  const isError = isLoading && userPagesError;

  if (isLoading) {
    return <UserPagesListSkeleton />;
  }
  if (isError) {
    return <UserPagesListSkeleton />;
  }

  return (
    <section className={`${bem('list-container')} ${isEmpty ? bem('list-container', 'empty') : ''}`}>
      <div className={bem('list')}>
        {pages?.map(page => (
          <UserPageCard onRemove={handleRemove} inkindPage={page} key={page.id} revalidateInkindPages={revalidate} />
        ))}
        <div className={bem('create-button-container')}>
          <Tooltip text="Add page">
            <Button type="a" externalLink href="/create" variant="secondary" circle size="xl">
              <FontIcon name="add" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </section>
  );
}

export default withSuspense(UserPagesList, UserPagesListSkeleton);
